/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../core/components';
import { Component } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GslbApiService } from '../../../../shared/http';
import { GslbRealServiceModel } from '../../../../shared/models';
import { LbErrorService } from '../../../../shared/services/components';
import {
    LanguageService,
    LoadingService,
    LogService,
    NavigationService,
    UtilsService
} from '../../../../../../core/services';
import { Observable, Subscriber } from 'rxjs';
import { ipValidator, nameValidation } from '../../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-add-edit-members',
    templateUrl: './addEditMembers.component.html'
})

export class AddEditMembersComponent extends BaseComponent {
    /**
     * Store the dropdown content for use in the view.
     */
    public dropdown: any = {
        membersWeight: [],
    };

    /**
     * Members form group.
     */
    public memberForm: FormGroup;

    /**
     * Our available made members from the API.
     */
    public members: GslbRealServiceModel[] = [];

    /**
     * Available members in the drag and drop table.
     */
    public available: any = [];

    /**
     * Selected members in the drag and drop table.
     */
    public selected: any = [];

    /**
     * To store the current member the user is editing.
     * @private
     */
    private currentMember: any = [];

    /**
     * Store initial data to compare against changes.
     * @private
     */
    private initialData: any = {};

    /**
     * Store the id of the member to edit.
     * @private
     */
    public editId: string;

    constructor(
        private formBuilder: FormBuilder,
        private gslbApiService: GslbApiService,
        private languageService: LanguageService,
        private lbErrorService: LbErrorService,
        private loadingService: LoadingService,
        private logService: LogService,
        private navigationService: NavigationService,
        private route: ActivatedRoute,
        private router: Router,
        private utilsService: UtilsService
    ) {
        super();

        this.setUpMembers();
    }

    /**
     * Save the member.
     */
    public submitMember(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const apiCall = this.getMembersAPICall();
            apiCall.pipe(takeUntil(this.destroyed$)).subscribe({
                next: (): void => {
                    this.navigateToURL('/cluster-configuration/gslb?sFilter=members');
                },
                error: (error: ErrorResponseModel): void => {
                    this.lbErrorService.pageWithErrors(this.memberForm, error.error.errors);
                    this.logService.error('GslbComponent: submitMember', 'Error submitting member', error);
                    this.loadingService.loadingButtonEmitter.next(false);
                },
                complete: (): void => {
                    observer.next();
                    observer.complete();
                }
            });
        });
    }

    /**
     * Get the member data for submitting.
     * @private
     */
    private getMembersAPICall(): any {
        const member = this.memberForm.getRawValue();
        const apiCall = this.editId
            ? this.gslbApiService.updateRealServer(member)
            : this.gslbApiService.addRealServer(member);
        if (this.editId) {
            member.id = this.editId;
        }

        return apiCall;
    }

    /**
     * Set up the member's data.
     * @private
     */
    private setUpMembers(): void {
        this.editId = this.route.snapshot.paramMap.get('id');
        this.setUpMembersForm();
        this.setupFormSubscriber();
        this.setUpGlobalNamesDropdownContent();
        this.getMembersData();
    }

    /**
     * Get members data from the API.
     * @private
     */
    private getMembersData(): void {
        this.gslbApiService.getRealServers()
            .pipe(takeUntil(this.destroyed$))
            .subscribe(members => {
                this.members = members;
                if (this.editId) {
                    this.patchDataToForm();
                }
            });
    }

    /**
     * Set edit data.
     * @private
     */
    private patchDataToForm(): void {
        this.currentMember = this.members.find(item => item.id === this.editId);
        this.initialData = this.currentMember;
        delete this.initialData['id'];
        this.memberForm.patchValue(this.currentMember);
    }

    /**
     * Set up the form.
     * @private
     */
    private setUpMembersForm(): void {
        this.memberForm = this.formBuilder.group({
            name: [null, [Validators.required, nameValidation]],
            ip: [null, [Validators.required, ipValidator]],
            monitorIp: [null, [Validators.required, ipValidator]],
            weight: [null, [Validators.required, Validators.min(0), Validators.max(10)]],
        });
    }

    /**
     * Setup form subscribers.
     * @private
     */
    private setupFormSubscriber(): void {
        this.memberForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: form => {
                    this.utilsService.debounce('form', () => {
                        this.validateMember('name', form['name']);
                        this.validateMember('ip', form['ip']);
                        if (this.editId
                            && !this.utilsService.hasFormDataChanged(this.initialData, form)) {
                            this.memberForm.markAsPristine();
                        } else {
                            this.memberForm.markAsDirty();
                        }
                    }, 250);
                }
            });
    }

    /**
     * Validate member name.
     * @param prop
     * @param value
     * @private
     */
    private validateMember(prop, value): void {
        const memberControl = this.memberForm.get(prop);
        let duplicateMember = this.members.find(member => member[prop] === value);
        if (this.editId && duplicateMember && duplicateMember.id === this.currentMember.id) {
            duplicateMember = undefined;
        }

        if (duplicateMember) {
            memberControl.setErrors({
                'duplicate':
                    `${this.languageService.translate('duplicate_member_prefix')}` +
                    `${this.languageService.translate(prop)}` + `${this.languageService.translate('already_exists')}`
            });
        }
    }

    /**
     * Set the pool arrays.
     * @private
     */
    private setUpGlobalNamesDropdownContent(): void {
        for (let i = 0; i < 11; i++) {
            this.dropdown.membersWeight.push(<DropdownModel>{ key: i, value: i.toString() });
        }
    }

    /**
     * Navigate to supplied URL.
     * @param url
     * @private
     */
    private navigateToURL(url): void {
        this.router.navigateByUrl(this.navigationService.getEnduranceUrl(url)).then().catch();
    }
}
